import { FocusLock } from '@chakra-ui/focus-lock'
import {
  Box,
  Button,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import * as Frigade from '@frigade/react'
import { useRouter } from 'next/router'
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import { isNullOrUndefined } from '@common/util/functions'
import { isNotNullOrUndefined } from '@common/util/isNotNullOrUndefined'
import { Z_INDEX_TOUR_CARD, Z_INDEX_TOUR_OVERLAY } from '~/theme/zIndex'
import {
  generateOverviewRoute,
  makeURLStringFromURLObject,
  OverviewRoute,
  PropertiesRoute,
} from '~/util/route'

/**
 * Frigade custom Flow that walks users through AI Insights and Ask Rentana.
 *
 * Create a Custom Flow in the Frigade Engage dashboard with this YAML (step ids must match),
 * then set `FRIGADE_AI_FEATURES_FLOW_ID` to the generated flow id:
 *
 * ```yaml
 * steps:
 *   - id: insights-intro
 *     title: Know your portfolio like never before.
 *     subtitle: Insights automatically surfaces what’s driving performance across your portfolio the moment you log in. No pulling reports, no piecing it together yourself.
 *     primaryButton:
 *       title: View Insights
 *     secondaryButton:
 *       title: Maybe later
 *       action: flow.skip
 *     # Optional image/GIF for the intro modal
 *     imageUri: https://example.com/insights-demo.gif
 *   - id: insights-tour
 *     title: Start your day already ahead
 *     subtitle: Insights surfaces what’s driving your portfolio the moment you log in.
 *     primaryButton:
 *       title: Next
 *     secondaryButton:
 *       title: Skip
 *       action: flow.skip
 *     selector: .frigade-ai-insights-anchor
 *   - id: ask-rentana
 *     title: Ask Rentana anything
 *     subtitle: Type a question in plain language. Get an answer grounded in your data, in seconds.
 *     primaryButton:
 *       title: Try it
 *     secondaryButton:
 *       title: Maybe later
 *       action: flow.skip
 *     selector: "#frigade-ask-rentana-anchor"
 * ```
 */
export const FRIGADE_AI_FEATURES_FLOW_ID = 'flow_cNpxlvTI'

export const FRIGADE_AI_INSIGHTS_ANCHOR_CLASS = 'frigade-ai-insights-anchor'
export const FRIGADE_AI_INSIGHTS_ANCHOR_SELECTOR = `.${FRIGADE_AI_INSIGHTS_ANCHOR_CLASS}`
export const FRIGADE_ASK_RENTANA_ANCHOR_ID = 'frigade-ask-rentana-anchor'
export const FRIGADE_ASK_RENTANA_ANCHOR_SELECTOR = `#${FRIGADE_ASK_RENTANA_ANCHOR_ID}`

const INSIGHTS_INTRO_STEP_ID = 'insights-intro'
const INSIGHTS_TOUR_STEP_ID = 'insights-tour'
const ASK_RENTANA_STEP_ID = 'ask-rentana'

const DEFAULT_INSIGHTS_CALLOUT = {
  title: 'Start your day already ahead',
  subtitle: 'Insights surfaces what’s driving your portfolio the moment you log in.',
  primaryButtonTitle: 'Next',
  secondaryButtonTitle: 'Skip',
}

const DEFAULT_INSIGHTS_MODAL = {
  title: 'Know your portfolio like never before.',
  subtitle:
    'Insights automatically surfaces what’s driving performance across your portfolio the moment you log in. No pulling reports, no piecing it together yourself.',
  primaryButtonTitle: 'View Insights',
  secondaryButtonTitle: 'Maybe later',
}

const DEFAULT_ASK_RENTANA = {
  title: 'Ask Rentana anything',
  subtitle: 'Type a question in plain language. Get an answer grounded in your data, in seconds.',
  primaryButtonTitle: 'Try it',
  secondaryButtonTitle: 'Maybe later',
}

const TOUR_CARD_GAP_PX = 12
const TOUR_CARD_MAX_WIDTH_PX = 320
const TOUR_CARD_VIEWPORT_MARGIN_PX = 12
/** Fallback until the card is measured; tall enough to prefer flipping over clipping. */
const TOUR_CARD_ESTIMATED_HEIGHT_PX = 180
const TOUR_SPOTLIGHT_PADDING_PX = 6
/**
 * Wait long enough for overview/property data fetches before deciding the insights
 * anchor will never mount (auto-completing the tour step). 750ms is too short and
 * skips the callout while KeyInsightCell is still waiting on portfolio properties.
 */
const INSIGHTS_TOUR_ANCHOR_GIVE_UP_MS = 10_000

type TourCalloutSide = 'right' | 'bottom'

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

function getTourCardMaxWidth(): number {
  if (typeof window === 'undefined') {
    return TOUR_CARD_MAX_WIDTH_PX
  }
  return Math.min(TOUR_CARD_MAX_WIDTH_PX, window.innerWidth - TOUR_CARD_VIEWPORT_MARGIN_PX * 2)
}

function getPathnameSegment(pathname: string): string {
  return pathname.replace(/^\//, '').split('/')[0] ?? ''
}

function useIsOnAiInsightsPage(): boolean {
  const router = useRouter()
  const path = getPathnameSegment(router.pathname)
  const view = typeof router.query.view === 'string' ? router.query.view : undefined

  if (path === 'overview') {
    return view === OverviewRoute.dashboard || isNullishView(view)
  }
  if (path === 'properties') {
    return view === PropertiesRoute.summary || isNullishView(view)
  }
  return false
}

function isNullishView(view: string | undefined): boolean {
  // `view` can be missing briefly during hydration; treat as the page default
  // (overview dashboard / property summary).
  return view === undefined
}

function useDoesAnchorExist(
  selector: string,
  isEnabled: boolean,
  settleMs = 750
): {
  doesExist: boolean
  hasSettled: boolean
} {
  const [doesExist, setDoesExist] = useState(false)
  const [hasSettled, setHasSettled] = useState(false)

  useEffect(() => {
    if (!isEnabled) {
      setDoesExist(false)
      setHasSettled(false)
      return
    }

    setHasSettled(false)
    const update = () => {
      setDoesExist(isNotNullOrUndefined(document.querySelector(selector)))
    }
    update()

    const observer = new MutationObserver(update)
    observer.observe(document.body, { childList: true, subtree: true })
    // Wait for data-driven anchors (e.g. overview key insights) before settling.
    const settleTimeout = window.setTimeout(() => setHasSettled(true), settleMs)

    return () => {
      observer.disconnect()
      window.clearTimeout(settleTimeout)
    }
  }, [selector, isEnabled, settleMs])

  return { doesExist, hasSettled }
}

/**
 * Track the anchored element’s viewport rect for spotlight + card positioning.
 */
function useAnchorRect(selector: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    const element = document.querySelector(selector)
    if (!(element instanceof HTMLElement)) {
      setRect(null)
      return
    }

    const update = () => setRect(element.getBoundingClientRect())
    update()

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(element)
    window.addEventListener('resize', update)
    // Capture scrolls from nested overflow containers (e.g. property summary).
    window.addEventListener('scroll', update, true)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [selector])

  return rect
}

function getTourCardPosition(
  rect: DOMRect,
  side: TourCalloutSide,
  cardWidth: number,
  cardHeight: number
): { top: number; left: number } {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const minLeft = TOUR_CARD_VIEWPORT_MARGIN_PX
  const minTop = TOUR_CARD_VIEWPORT_MARGIN_PX
  const maxLeft = viewportWidth - TOUR_CARD_VIEWPORT_MARGIN_PX - cardWidth
  const maxTop = viewportHeight - TOUR_CARD_VIEWPORT_MARGIN_PX - cardHeight

  if (side === 'right') {
    const preferredLeft = rect.right + TOUR_CARD_GAP_PX
    const doesOverflowRight =
      preferredLeft + cardWidth > viewportWidth - TOUR_CARD_VIEWPORT_MARGIN_PX
    const left = doesOverflowRight ? rect.left - TOUR_CARD_GAP_PX - cardWidth : preferredLeft
    const top = rect.top + rect.height / 2 - cardHeight / 2
    return {
      top: clamp(top, minTop, maxTop),
      left: clamp(left, minLeft, maxLeft),
    }
  }

  const preferredTop = rect.bottom + TOUR_CARD_GAP_PX
  const doesOverflowBottom =
    preferredTop + cardHeight > viewportHeight - TOUR_CARD_VIEWPORT_MARGIN_PX
  const top = doesOverflowBottom ? rect.top - TOUR_CARD_GAP_PX - cardHeight : preferredTop
  return {
    top: clamp(top, minTop, maxTop),
    left: clamp(rect.left, minLeft, maxLeft),
  }
}

function NewBadge() {
  return (
    <Text
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="wider"
      textTransform="uppercase"
      color="accent"
    >
      NEW
    </Text>
  )
}

function TourCalloutCard({
  title,
  subtitle,
  primaryButtonTitle,
  secondaryButtonTitle,
  primaryButtonRef,
  onPrimary,
  onSecondary,
}: {
  title: string
  subtitle: string
  primaryButtonTitle: string
  secondaryButtonTitle: string
  primaryButtonRef?: RefObject<HTMLButtonElement>
  onPrimary: () => void
  onSecondary: () => void
}) {
  return (
    <VStack
      align="stretch"
      spacing="3"
      bg="bg-surface"
      borderWidth="1px"
      borderColor="border"
      borderRadius="lg"
      boxShadow="lg"
      px="5"
      py="4"
      w="full"
    >
      <VStack align="start" spacing="1">
        <NewBadge />
        <Heading size="sm">{title}</Heading>
        <Text fontSize="sm" color="muted">
          {subtitle}
        </Text>
      </VStack>
      <HStack spacing="2" justify="flex-end">
        <Button variant="ghost" size="sm" onClick={onSecondary}>
          {secondaryButtonTitle}
        </Button>
        <Button ref={primaryButtonRef} variant="primary" size="sm" onClick={onPrimary}>
          {primaryButtonTitle}
        </Button>
      </HStack>
    </VStack>
  )
}

/**
 * Spotlight cutout + card, portaled to `document.body` so we escape Chakra’s
 * `portalZIndex={99}` stacking context and sit above sticky page toppers.
 *
 * The dim layer uses a box-shadow hole over the anchor (rather than elevating the
 * anchor itself), which works even when the anchor lives inside another stacking
 * context like the sidebar.
 */
function TourCallout({
  anchorSelector,
  side,
  title,
  subtitle,
  primaryButtonTitle,
  secondaryButtonTitle,
  onPrimary,
  onSecondary,
}: {
  anchorSelector: string
  side: TourCalloutSide
  title: string
  subtitle: string
  primaryButtonTitle: string
  secondaryButtonTitle: string
  onPrimary: () => void
  onSecondary: () => void
}) {
  const rect = useAnchorRect(anchorSelector)
  const cardRef = useRef<HTMLDivElement>(null)
  const primaryButtonRef = useRef<HTMLButtonElement>(null)
  const [cardSize, setCardSize] = useState({
    width: getTourCardMaxWidth(),
    height: TOUR_CARD_ESTIMATED_HEIGHT_PX,
  })

  useLayoutEffect(() => {
    if (isNullOrUndefined(cardRef.current)) {
      return
    }
    const { width, height } = cardRef.current.getBoundingClientRect()
    setCardSize(previous =>
      previous.width === width && previous.height === height ? previous : { width, height }
    )
  }, [rect, title, subtitle, primaryButtonTitle, secondaryButtonTitle])

  if (isNullOrUndefined(rect) || typeof document === 'undefined') {
    return null
  }

  const cardMaxWidth = getTourCardMaxWidth()
  const cardPosition = getTourCardPosition(rect, side, cardSize.width, cardSize.height)
  const spotlightTop = rect.top - TOUR_SPOTLIGHT_PADDING_PX
  const spotlightLeft = rect.left - TOUR_SPOTLIGHT_PADDING_PX
  const spotlightWidth = rect.width + TOUR_SPOTLIGHT_PADDING_PX * 2
  const spotlightHeight = rect.height + TOUR_SPOTLIGHT_PADDING_PX * 2

  return createPortal(
    <>
      {/* Click-catcher behind the spotlight hole */}
      <Box
        position="fixed"
        inset={0}
        zIndex={Z_INDEX_TOUR_OVERLAY}
        onClick={onSecondary}
        aria-hidden
      />
      {/* Transparent hole + shadow = page dim with cutout around the anchor */}
      <Box
        position="fixed"
        top={`${spotlightTop}px`}
        left={`${spotlightLeft}px`}
        width={`${spotlightWidth}px`}
        height={`${spotlightHeight}px`}
        borderRadius="lg"
        boxShadow="0 0 0 9999px rgba(0, 0, 0, 0.6)"
        borderWidth="2px"
        borderColor="accent"
        zIndex={Z_INDEX_TOUR_OVERLAY}
        pointerEvents="none"
        aria-hidden
      />
      <FocusLock restoreFocus autoFocus initialFocusRef={primaryButtonRef}>
        <Box
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          position="fixed"
          top={`${cardPosition.top}px`}
          left={`${cardPosition.left}px`}
          zIndex={Z_INDEX_TOUR_CARD}
          maxW={`${cardMaxWidth}px`}
          outline="none"
        >
          <TourCalloutCard
            title={title}
            subtitle={subtitle}
            primaryButtonTitle={primaryButtonTitle}
            secondaryButtonTitle={secondaryButtonTitle}
            primaryButtonRef={primaryButtonRef}
            onPrimary={onPrimary}
            onSecondary={onSecondary}
          />
        </Box>
      </FocusLock>
    </>,
    document.body
  )
}

function InsightsModal({
  title,
  subtitle,
  imageUri,
  primaryButtonTitle,
  secondaryButtonTitle,
  onPrimary,
  onSecondary,
}: {
  title: string
  subtitle: string
  imageUri?: string
  primaryButtonTitle: string
  secondaryButtonTitle: string
  onPrimary: () => void
  onSecondary: () => void
}) {
  const primaryButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <Modal isOpen onClose={onSecondary} isCentered size="md" initialFocusRef={primaryButtonRef}>
      <ModalOverlay />
      <ModalContent>
        <ModalBody pt="6">
          <VStack align="start" spacing="3">
            {isNotNullOrUndefined(imageUri) && (
              <Box borderRadius="md" overflow="hidden" w="full">
                <Image src={imageUri} alt="" w="full" />
              </Box>
            )}
            <Heading size="md">{title}</Heading>
            <Text color="muted">{subtitle}</Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing="2">
            <Button variant="ghost" onClick={onSecondary}>
              {secondaryButtonTitle}
            </Button>
            <Button ref={primaryButtonRef} variant="primary" onClick={onPrimary}>
              {primaryButtonTitle}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

/**
 * Custom Frigade Engage flow for the AI Insights + Ask Rentana feature launch.
 * Must be rendered inside `<Frigade.Provider>`.
 */
export function FrigadeAiFeaturesFlow() {
  const router = useRouter()
  const { flow } = Frigade.useFlow(FRIGADE_AI_FEATURES_FLOW_ID)
  const isOnAiInsightsPage = useIsOnAiInsightsPage()
  const step = flow?.getCurrentStep()
  const stepId = step?.id
  const isInsightsTourStepVisible = flow?.isVisible === true && stepId === INSIGHTS_TOUR_STEP_ID
  const isAskRentanaStepVisible = flow?.isVisible === true && stepId === ASK_RENTANA_STEP_ID
  // Only observe once the tour step is active and we’re on an insights page so settle
  // resets after navigation from the intro modal / off-route redirect.
  const { doesExist: doesInsightsAnchorExist, hasSettled: hasInsightsAnchorSettled } =
    useDoesAnchorExist(
      FRIGADE_AI_INSIGHTS_ANCHOR_SELECTOR,
      isInsightsTourStepVisible && isOnAiInsightsPage,
      INSIGHTS_TOUR_ANCHOR_GIVE_UP_MS
    )
  const { doesExist: doesAskRentanaAnchorExist } = useDoesAnchorExist(
    FRIGADE_ASK_RENTANA_ANCHOR_SELECTOR,
    isAskRentanaStepVisible
  )

  useEffect(() => {
    if (!flow?.isVisible || isNullOrUndefined(step)) {
      return
    }
    step.start().catch(error => console.error(error))
  }, [flow, step, stepId])

  // Keep the tour on an insights page so the callout can attach (same destination as intro CTA).
  useEffect(() => {
    if (!isInsightsTourStepVisible || isOnAiInsightsPage) {
      return
    }
    const portfolioSid =
      typeof router.query.portfolioSid === 'string' ? router.query.portfolioSid : undefined
    void router.push(
      makeURLStringFromURLObject(generateOverviewRoute(OverviewRoute.dashboard, { portfolioSid }))
    )
  }, [isInsightsTourStepVisible, isOnAiInsightsPage, router])

  // If the insights UI never mounts on the tour step, advance instead of stalling.
  useEffect(() => {
    if (
      !isInsightsTourStepVisible ||
      !isOnAiInsightsPage ||
      !hasInsightsAnchorSettled ||
      doesInsightsAnchorExist ||
      isNullOrUndefined(step)
    ) {
      return
    }
    step.complete().catch(error => console.error(error))
  }, [
    isInsightsTourStepVisible,
    isOnAiInsightsPage,
    hasInsightsAnchorSettled,
    doesInsightsAnchorExist,
    step,
  ])

  if (!flow?.isVisible || isNullOrUndefined(step)) {
    return null
  }

  const skipFlow = () => {
    flow.skip().catch(error => console.error(error))
  }

  const completeStep = () => {
    step.complete().catch(error => console.error(error))
  }

  if (stepId === INSIGHTS_INTRO_STEP_ID) {
    return (
      <InsightsModal
        title={step.title ?? DEFAULT_INSIGHTS_MODAL.title}
        subtitle={step.subtitle ?? DEFAULT_INSIGHTS_MODAL.subtitle}
        imageUri={typeof step.imageUri === 'string' ? step.imageUri : undefined}
        primaryButtonTitle={step.primaryButton?.title ?? DEFAULT_INSIGHTS_MODAL.primaryButtonTitle}
        secondaryButtonTitle={
          step.secondaryButton?.title ?? DEFAULT_INSIGHTS_MODAL.secondaryButtonTitle
        }
        onPrimary={() => {
          completeStep()
          // Stay put when already on an insights-eligible page (anchor may still be
          // loading); only navigate when the tour redirect effect would also navigate.
          if (isOnAiInsightsPage) {
            return
          }
          const portfolioSid =
            typeof router.query.portfolioSid === 'string' ? router.query.portfolioSid : undefined
          void router.push(
            makeURLStringFromURLObject(
              generateOverviewRoute(OverviewRoute.dashboard, { portfolioSid })
            )
          )
        }}
        onSecondary={skipFlow}
      />
    )
  }

  if (stepId === INSIGHTS_TOUR_STEP_ID) {
    if (isOnAiInsightsPage && doesInsightsAnchorExist) {
      return (
        <TourCallout
          anchorSelector={FRIGADE_AI_INSIGHTS_ANCHOR_SELECTOR}
          side="bottom"
          title={step.title ?? DEFAULT_INSIGHTS_CALLOUT.title}
          subtitle={step.subtitle ?? DEFAULT_INSIGHTS_CALLOUT.subtitle}
          primaryButtonTitle={
            step.primaryButton?.title ?? DEFAULT_INSIGHTS_CALLOUT.primaryButtonTitle
          }
          secondaryButtonTitle={
            step.secondaryButton?.title ?? DEFAULT_INSIGHTS_CALLOUT.secondaryButtonTitle
          }
          onPrimary={completeStep}
          onSecondary={skipFlow}
        />
      )
    }
    // Waiting for navigation / redirect and/or insights UI; settle-without-anchor completes via effect.
    return null
  }

  if (stepId === ASK_RENTANA_STEP_ID) {
    if (!doesAskRentanaAnchorExist) {
      return null
    }

    return (
      <TourCallout
        anchorSelector={FRIGADE_ASK_RENTANA_ANCHOR_SELECTOR}
        side="right"
        title={step.title ?? DEFAULT_ASK_RENTANA.title}
        subtitle={step.subtitle ?? DEFAULT_ASK_RENTANA.subtitle}
        primaryButtonTitle={step.primaryButton?.title ?? DEFAULT_ASK_RENTANA.primaryButtonTitle}
        secondaryButtonTitle={
          step.secondaryButton?.title ?? DEFAULT_ASK_RENTANA.secondaryButtonTitle
        }
        onPrimary={() => {
          const askRentanaButton = document.querySelector<HTMLElement>(
            FRIGADE_ASK_RENTANA_ANCHOR_SELECTOR
          )
          askRentanaButton?.click()
          completeStep()
        }}
        onSecondary={skipFlow}
      />
    )
  }

  return null
}
